import { supabase } from '../../config/supabase';
import {
    createNotificationWithoutPush,
    triggerPushForNotification,
    NOTIFICATION_TYPES,
} from './notifications';
import { computeEstimationTotals } from './finalPricingModes';

// Matches the hardcoded production origin QuotationEmailSender.js already
// uses for its own customer-facing links (contactUrl) — this codebase
// doesn't derive email links from window.location.origin since the email is
// as likely to be opened outside the app as inside it.
const SITE_BASE_URL = 'https://app.notifyapp.com.au';

const DEFAULT_LOGO_URL = 'https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/wide-logo-black.svg';

// Same enterprise-logo-or-default resolution QuotationEmailSender.js and
// JobBookedDatesEmailSender.js already use for their own emails.
const getCompanyLogoUrl = (company) =>
    company?.subscription_plan === 'enterprise' && company?.enterprise_logo_url
        ? company.enterprise_logo_url
        : DEFAULT_LOGO_URL;

/**
 * Fires the send-staff-notification-email edge function. Failures are
 * swallowed — this is a best-effort second channel alongside push, never
 * something that should block the caller's actual save flow (pricing entry /
 * final pricing save).
 *
 * `jobUrl` always points at the job's own assignment page. `secondUrl` /
 * `secondLabel` point at whatever the recipient should do next for this
 * notification type (enter final pricing, or go quote it) — the edge
 * function renders it as a second email button only when both are present.
 * `pricingTable`, if present, is rendered as a $ breakdown under the message
 * — see notifyPricingEntered/notifyReadyForQuotation for its shape.
 *
 * Field names on the invoke body are camelCase, matching the other two
 * edge functions this app already calls (send-quotation-email-postmark,
 * send-booked-dates-email-postmark) rather than snake_case.
 */
const sendStaffNotificationEmail = async ({ userId, companyId, title, message, jobUrl, secondUrl, secondLabel, logoUrl, companyName, pricingTable }) => {
    try {
        const { error } = await supabase.functions.invoke('send-staff-notification-email', {
            body: {
                userId,
                companyId,
                title,
                message,
                jobUrl,
                secondUrl: secondUrl || null,
                secondLabel: secondLabel || null,
                logoUrl,
                companyName: companyName || null,
                pricingTable: pricingTable || null,
            },
        });

        if (error) throw error;
    } catch (error) {
        console.error('Failed to send staff notification email:', error);
    }
};

const notifyUser = async ({ userId, companyId, type, title, message, jobId, actionUrl, jobUrl, secondUrl, secondLabel, logoUrl, companyName, pricingTable, metadata }) => {
    try {
        const notification = await createNotificationWithoutPush({
            user_id: userId,
            company_id: companyId,
            type,
            title,
            message,
            related_type: 'job',
            related_id: jobId,
            action_url: actionUrl,
            metadata: metadata || null,
        });

        await Promise.all([
            triggerPushForNotification(notification.id),
            sendStaffNotificationEmail({ userId, companyId, title, message, jobUrl, secondUrl, secondLabel, logoUrl, companyName, pricingTable }),
        ]);
    } catch (error) {
        console.error('Failed to notify user of pricing event:', userId, error);
    }
};

const uniqueUserIds = (userIds) =>
    Array.isArray(userIds) ? [...new Set(userIds.filter(Boolean))] : [];

/**
 * Shared gating + dedup + fan-out for both pricing notification triggers
 * below. Owns the try/catch at its own function boundary (rather than
 * relying solely on notifyUser's internal safety net) so the "must never
 * throw into the caller's save flow" guarantee is enforced directly here —
 * matching the pattern used by sendEstimationCompletedNotifications and
 * createNotificationWithFallback elsewhere in this module.
 */
const notifyManyUsers = async ({ settingFlag, userIds, job, company, type, title, message, actionUrl, jobUrl, secondUrl, secondLabel, pricingTable, metadata }) => {
    try {
        const settings = company?.settings?.pricing_settings || {};
        if (!settings[settingFlag]) return;

        const ids = uniqueUserIds(userIds);
        if (ids.length === 0 || !job?.id || !company?.id) return;

        const logoUrl = getCompanyLogoUrl(company);
        const companyName = company?.name || null;

        await Promise.all(ids.map(userId => notifyUser({
            userId,
            companyId: company.id,
            type,
            title,
            message,
            jobId: job.id,
            actionUrl,
            jobUrl,
            secondUrl,
            secondLabel,
            logoUrl,
            companyName,
            pricingTable,
            metadata,
        })));
    } catch (error) {
        console.error('Failed to notify users of pricing event:', error);
    }
};

/**
 * SingleAssignment.js's "was pricing already complete before this save"
 * baseline is derived from local form state (pricingValues), which starts
 * empty on every page load rather than being hydrated from previously-saved
 * job_pricing_entries rows — a pre-existing gap in that component, not
 * something introduced here. That means the false -> true transition it
 * detects can, in some cases (e.g. reopening an already-fully-priced job and
 * saving again), fire more than once for the same job/revision. Rather than
 * fix that deeper, pre-existing form-state issue as part of this
 * notification feature, this guard makes the notification itself idempotent
 * per (job, quote_revision): if one was already sent for this job on this
 * revision, skip firing another, regardless of how many times the caller's
 * transition check re-detects "complete."
 *
 * Scoped per revision (not just per job) so that starting a new quote
 * revision (PricingReviewDropdown.js's handleUpdateQuote) and re-completing
 * pricing on it fires a fresh notification, same as the original revision
 * did — a revision is effectively a new round of pricing and recipients
 * should hear about each one.
 *
 * `metadata.quote_revision` is read client-side rather than filtered in the
 * query — per-job row counts here are tiny (at most one per revision this
 * job has ever had), and it lets a legacy row saved before this field
 * existed (metadata null) be treated as revision 1, so already-notified
 * revision-1 jobs don't get a spurious duplicate the first time this ships.
 */
const alreadyNotifiedPricingEntered = async ({ jobId, companyId, quoteRevision = 1 }) => {
    if (!jobId || !companyId) return false;

    const { data, error } = await supabase
        .from('notifications')
        .select('id, metadata')
        .eq('company_id', companyId)
        .eq('related_type', 'job')
        .eq('related_id', jobId)
        .eq('type', NOTIFICATION_TYPES.PRICING_ENTERED);

    if (error) {
        console.error('Failed to check for existing pricing-entered notification:', error);
        return false;
    }

    const revision = Number(quoteRevision) || 1;

    return (data || []).some(row => (Number(row?.metadata?.quote_revision) || 1) === revision);
};

/**
 * Builds the "Estimation Pricing" table for notifyPricingEntered's email —
 * one row per individual field entered (e.g. "Roof Trusses", "Labour",
 * "Ground Floor Walls"), not summed into Roof/Wall/Floor department totals.
 * Uses `computeEstimationTotals(...).estimation.lines` — the estimator's own
 * raw line items, already labeled and unit-tagged by that shared helper —
 * rather than the merged `quoting` bucket, since this notification is
 * specifically about what the estimator just entered, not any later
 * department review.
 */
const buildEstimationPricingTable = (pricingEntries) => {
    const { estimation } = computeEstimationTotals(pricingEntries || []);
    const hasMultipleUnits = new Set(estimation.lines.map(line => line.unit_number)).size > 1;

    return {
        title: 'Estimation Pricing',
        rows: estimation.lines.map(line => ({
            label: hasMultipleUnits ? `Unit ${line.unit_number} · ${line.label}` : line.label,
            amount: line.value,
        })),
        total: estimation.grandTotal || 0,
        notes: null,
    };
};

/**
 * Fires when a job's estimator pricing transitions from incomplete to
 * complete (all configured required $ fields filled) — see
 * SingleAssignment.js's hasAllRequiredPricingValues(). Gated on
 * company.settings.pricing_settings.send_email_on_estimation_complete,
 * recipients from estimation_complete_email_user_ids — reviving a setting
 * that already existed in Settings.js but was never wired to anything.
 * Fires at most once per (job, quote_revision) — see
 * alreadyNotifiedPricingEntered above.
 *
 * `quoteRevision` identifies which round of pricing just completed —
 * SingleAssignment.js's simple estimator form always writes revision 1 (it
 * has no revision concept of its own), while PricingReviewDropdown.js passes
 * its current `activeRevision`. Stored on the created notification's
 * `metadata` so a later revision re-completing pricing isn't seen as
 * already-notified.
 *
 * `pricingEntries` — the job's current job_pricing_entries rows (both
 * `type: 'estimation'` and `type: 'department'`, so computeEstimationTotals
 * can do its usual per-field merge) — is fetched fresh by the caller right
 * before invoking this, rather than reused from local component state,
 * since that state isn't guaranteed to include every previously-saved unit
 * by the time this fires (see SingleAssignment.js's save handler).
 */
export const notifyPricingEntered = async ({ job, company, companySlug, userIds, pricingEntries, quoteRevision = 1 }) => {
    // Check the settings flag before the idempotency query, not after — no
    // reason to pay a DB round-trip on every detected transition for
    // companies that have never enabled this notification at all.
    if (!company?.settings?.pricing_settings?.send_email_on_estimation_complete) return;

    const revision = Number(quoteRevision) || 1;

    if (await alreadyNotifiedPricingEntered({ jobId: job?.id, companyId: company?.id, quoteRevision: revision })) return;

    await notifyManyUsers({
        settingFlag: 'send_email_on_estimation_complete',
        userIds,
        job,
        company,
        type: NOTIFICATION_TYPES.PRICING_ENTERED,
        title: 'Pricing entered',
        message: `All required pricing has been entered for job ${job?.unique_id}${revision > 1 ? ` (Revision ${revision})` : ''}.`,
        actionUrl: `/${companySlug}/assignment/${job?.id}`,
        jobUrl: `${SITE_BASE_URL}/${companySlug}/assignment/${job?.id}`,
        secondUrl: `${SITE_BASE_URL}/${companySlug}/final-pricings/${job?.id}`,
        secondLabel: 'Enter Final Pricing',
        pricingTable: buildEstimationPricingTable(pricingEntries),
        metadata: { quote_revision: revision },
    });
};

/**
 * Fires per department the first time its final price is entered in
 * FinalPricingDetails.js — only reachable when
 * final_pricing_mode === 'entered_final_pricing' (manual save doesn't exist
 * in the other 3 modes). Gated on
 * company.settings.pricing_settings.notify_final_pricing_entered,
 * recipients from final_pricing_entered_notify_user_ids (new setting).
 * Each department fires its own separate notification the first time it's
 * priced — this function itself has no idempotency check, so the caller
 * (FinalPricingDetails.js's save()) is responsible for only invoking it when
 * the department had no existing final price before that save; subsequent
 * edits to an already-entered final price must not re-fire it.
 *
 * `department` (the raw department key/id, as opposed to `departmentLabel`,
 * its display text) is used for the email's "Go to Quotation" button — it's
 * passed as a `department` query param so the Quotation page (a list, not a
 * per-job route like final-pricings) can deep-link straight to this job with
 * this department preselected in the send modal.
 *
 * `finalPrice` / `notes` are the just-saved job_final_pricings row's values
 * (FinalPricingDetails.js has them straight from saveFinalPricing's
 * response) — shown as a one-row "Final Pricing" table so the recipient
 * sees the actual quoted number in the email itself, not just a link to go
 * find it.
 */
export const notifyReadyForQuotation = async ({ job, department, departmentLabel, company, companySlug, userIds, finalPrice, notes }) => {
    await notifyManyUsers({
        settingFlag: 'notify_final_pricing_entered',
        userIds,
        job,
        company,
        type: NOTIFICATION_TYPES.READY_FOR_QUOTATION,
        title: 'Ready for quotation',
        message: `${departmentLabel} final pricing was entered for job ${job?.unique_id} — ready to quote.`,
        actionUrl: `/${companySlug}/final-pricings/${job?.id}`,
        jobUrl: `${SITE_BASE_URL}/${companySlug}/assignment/${job?.id}`,
        secondUrl: `${SITE_BASE_URL}/${companySlug}/quotations?jobId=${job?.id}${department ? `&department=${encodeURIComponent(department)}` : ''}`,
        secondLabel: 'Go to Quotation',
        pricingTable: {
            title: 'Final Pricing',
            rows: [{ label: departmentLabel, amount: Number(finalPrice) || 0 }],
            total: null,
            notes: notes || null,
        },
    });
};
